import {
  type MouseEventHandler,
  type ComponentPropsWithRef,
  type ComponentPropsWithoutRef,
  type ReactNode,
} from 'react';

import cx from 'classnames';

import InfoCircleFilled from 'components/shared/Icon/icons/InfoCircleFilled';
import { type AllOrNothing } from 'utils/types';

import s from './index.module.css';

/*
  Figma Reference
  https://www.figma.com/file/Fo1EZ0uHWvh9hWVNnoFtRj/Design-System--%3E-Vol-2?type=design&node-id=147-1951&mode=design&t=Okt9dKhHqtzohy9T-4
*/

export type SwitchCommonProps = {
  onClick: MouseEventHandler;
  active?: boolean;
  intermediate?: boolean;
  size?: 'default' | 'small';
  switchProps?: ComponentPropsWithRef<'div'>;
} & ComponentPropsWithoutRef<'button'>;

export type WithLabel =
  | {
      label: string;
      labelProps?: ComponentPropsWithRef<'span'>;
    }
  | {
      label: ReactNode;
      labelProps?: never;
    };
export type WithHint =
  | {
      hint: string;
      hintProps?: ComponentPropsWithRef<'span'> & {
        iconProps?: ComponentPropsWithRef<'svg'>;
      };
    }
  | {
      hint: ReactNode;
      hintProps?: never;
    };

type SwitchProps = SwitchCommonProps & AllOrNothing<WithLabel> & AllOrNothing<WithHint>;

function Switch(props: SwitchProps) {
  const {
    onClick,
    className,
    active = false,
    intermediate = false,
    size = 'default',
    switchProps = {},
    label,
    labelProps = {},
    hint: hintProp,
    hintProps = {},
    ...restProps
  } = props;

  const { className: switchClassName, ...restSwitchProps } = switchProps;
  const { className: labelClassName, ...restLabelProps } = labelProps;
  const { className: hintClassName, iconProps, ...restHintProps } = hintProps;
  const { className: hintIconClassName, ...restIconProps } = iconProps || {};

  return (
    <button
      onClick={onClick}
      type="button"
      className={cx(s.button, className, {
        [s.small]: size === 'small',
        [s.active]: active,
        [s.intermediate]: intermediate,
      })}
      {...restProps}
    >
      <div className={cx(s.switch, switchClassName)} {...restSwitchProps} />
      {typeof label === 'string' ? (
        <span className={cx(s.label, labelClassName)} {...restLabelProps}>
          {label}
        </span>
      ) : (
        label
      )}
      {typeof hintProp === 'string' ? (
        <span className={cx(s.hint, hintClassName)} {...restHintProps}>
          <InfoCircleFilled
            width="1.6rem"
            height="1.6rem"
            className={cx(s.hintIcon, hintIconClassName)}
            {...restIconProps}
          />
          {hintProp}
        </span>
      ) : (
        hintProp
      )}
    </button>
  );
}

export default Switch;
